import { useRef, useEffect } from 'react';
import { usePrefersReducedMotion } from '@/hooks/usePrefersReducedMotion';

// Realistic bubble parameters with diverse sizes
const BUBBLE_COUNT = 50;
const BUBBLE_MIN_RADIUS = 0.8;
const BUBBLE_MAX_RADIUS = 6;
const RISE_SPEED = 0.6;
const WOBBLE_AMOUNT = 0.3;

interface Bubble {
  x: number;
  y: number;
  radius: number;
  wobbleOffset: number;
  speed: number;
  alpha: number;
}

const Bubbles = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const prefersReducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let animationFrameId: number;
    const bubbles: Bubble[] = [];

    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    const createBubbles = () => {
      bubbles.length = 0;
      for (let i = 0; i < BUBBLE_COUNT; i++) {
        // Realistic bubble size distribution: mostly small, fewer large
        // Use power distribution for natural look
        const sizeRandom = Math.pow(Math.random(), 2); // Bias toward smaller bubbles
        const radius = BUBBLE_MIN_RADIUS + sizeRandom * (BUBBLE_MAX_RADIUS - BUBBLE_MIN_RADIUS);

        bubbles.push({
          x: Math.random() * canvas.width,
          y: canvas.height + Math.random() * canvas.height,
          radius,
          wobbleOffset: Math.random() * Math.PI * 2,
          // Larger bubbles rise faster (physics!)
          speed: RISE_SPEED + (radius / BUBBLE_MAX_RADIUS) * 0.5,
          // Larger bubbles more visible
          alpha: 0.4 + (radius / BUBBLE_MAX_RADIUS) * 0.3,
        });
      }
    };

    resizeCanvas();
    createBubbles();

    const handleResize = () => {
      resizeCanvas();
      createBubbles();
    };

    window.addEventListener('resize', handleResize);

    if (prefersReducedMotion) {
      // Draw static bubbles if motion is disabled
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      bubbles.forEach((b) => {
        ctx.beginPath();
        ctx.arc(b.x, b.y, b.radius, 0, Math.PI * 2);
        // Aqua/cyan color
        ctx.fillStyle = `rgba(0, 255, 255, ${b.alpha * 0.5})`;
        ctx.fill();
        ctx.strokeStyle = `rgba(255, 255, 255, ${b.alpha * 0.3})`;
        ctx.lineWidth = 0.5;
        ctx.stroke();
      });
      return;
    }

    const animate = (time: number) => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      bubbles.forEach((b) => {
        // Rise upward
        b.y -= b.speed;

        // Wobble horizontally (underwater drift)
        const wobble = Math.sin(time * 0.001 + b.wobbleOffset) * WOBBLE_AMOUNT;
        const x = b.x + wobble;

        // Reset bubble if it goes off-screen
        if (b.y < -b.radius * 2) {
          b.y = canvas.height + b.radius;
          b.x = Math.random() * canvas.width;
        }

        // Draw bubble with gradient for depth - darker colors for light mode visibility
        const gradient = ctx.createRadialGradient(
          x - b.radius * 0.3,
          b.y - b.radius * 0.3,
          0,
          x,
          b.y,
          b.radius
        );
        gradient.addColorStop(0, `rgba(100, 200, 220, ${b.alpha})`);
        gradient.addColorStop(0.5, `rgba(50, 150, 200, ${b.alpha * 0.8})`);
        gradient.addColorStop(1, `rgba(0, 100, 150, ${b.alpha * 0.6})`);

        ctx.beginPath();
        ctx.arc(x, b.y, b.radius, 0, Math.PI * 2);
        ctx.fillStyle = gradient;
        ctx.fill();

        // Subtle highlight
        ctx.strokeStyle = `rgba(255, 255, 255, ${b.alpha * 0.4})`;
        ctx.lineWidth = 0.5;
        ctx.stroke();
      });

      animationFrameId = requestAnimationFrame(animate);
    };

    animate(0);

    return () => {
      window.removeEventListener('resize', handleResize);
      cancelAnimationFrame(animationFrameId);
    };
  }, [prefersReducedMotion]);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
      }}
      aria-hidden="true"
    />
  );
};

export default Bubbles;
